import { forwardRef } from 'react';
import { Box, BoxProps } from './box';

/**
 * FlexColumn component - A convenience wrapper for vertical flex layouts.
 *
 * Pre-configured with `display="flex"`, `flexDirection="column"`, and `gap={1}`.
 * Used in ~20% of Box patterns in mobile app for vertical stacking.
 *
 * @example
 * // Form fields stacked vertically
 * <FlexColumn>
 *   <TextField label="Name" />
 *   <TextField label="Email" />
 *   <Button>Submit</Button>
 * </FlexColumn>
 *
 * @example
 * // Custom gap
 * <FlexColumn gap={2}>
 *   <Typography variant="h6">Title</Typography>
 *   <Typography variant="body2">Description</Typography>
 * </FlexColumn>
 *
 * @example
 * // Full width items
 * <FlexColumn gap={1} width="100%">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 * </FlexColumn>
 */
export interface FlexColumnProps extends BoxProps {
  /**
   * Gap between flex items (theme spacing units).
   * @default 1 (8px)
   */
  gap?: BoxProps['gap'];
}

export const FlexColumn = forwardRef<HTMLDivElement, FlexColumnProps>(
  ({ gap = 1, ...props }, ref) => (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Box ref={ref as any} display="flex" flexDirection="column" gap={gap} {...props} />
  )
);

FlexColumn.displayName = 'FlexColumn';
